import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, UserRole } from "@/hooks/useAuth";

interface RegisterFormProps {
  onSwitch: () => void;
}

type SignupMethod = "email" | "phone";

const roles: { value: UserRole; label: string; icon: string; desc: string; activeClass: string }[] = [
  { value: "buyer", label: "Buyer", icon: "ri-shopping-bag-line", desc: "Shop from verified factories & local sellers", activeClass: "border-orange-400 bg-orange-50" },
  { value: "seller", label: "Factory / Warehouse", icon: "ri-store-2-line", desc: "List products, manage orders & get verified", activeClass: "border-green-400 bg-green-50" },
  { value: "influencer", label: "Reseller / Star", icon: "ri-star-line", desc: "Promote products & earn commissions", activeClass: "border-rose-400 bg-rose-50" },
];

const platforms = ["TikTok", "YouTube", "Twitter / X", "Instagram", "Other"];

export default function RegisterForm({ onSwitch }: RegisterFormProps) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState<SignupMethod>("email");
  const [role, setRole] = useState<UserRole>("buyer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [platform, setPlatform] = useState("TikTok");
  const [followers, setFollowers] = useState("");
  const [handle, setHandle] = useState("");
  const [agree, setAgree] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const sendOtp = () => {
    if (!phone || phone.length < 7) { setError("Please enter a valid phone number."); return; }
    setError("");
    setOtpLoading(true);
    setTimeout(() => { setOtpSent(true); setOtpLoading(false); }, 1000);
  };

  const handleNext = () => {
    setError("");
    if (step === 1) { setStep(2); return; }

    if (!name) { setError("Please enter your full name."); return; }
    if (method === "email") {
      if (!email) { setError("Please enter your email address."); return; }
      if (!password || password.length < 6) { setError("Password must be at least 6 characters."); return; }
    } else {
      if (!otpSent || otp.length < 4) { setError("Please verify your phone number first."); return; }
    }
    if (!agree) { setError("Please agree to the Terms of Service."); return; }

    setLoading(true);
    setTimeout(() => {
      const newUser = {
        id: `u_${Date.now()}`,
        name: role === "seller" ? (storeName || name) : name,
        email: method === "email" ? email : `${phone}@phone.user`,
        role,
        avatar: `https://public.readdy.ai/ai/img_res/8baa7177abccdca3d80a6c570532fc9f.jpg${Date.now()}&orientation=squarish`,
        verified: false,
        ...(role === "seller" && { storeName: storeName || name }),
        ...(role === "influencer" && { platform: platform.toLowerCase(), followers }),
      };
      login(newUser);
      if (role === "seller") navigate("/dashboard");
      else navigate("/");
      setLoading(false);
    }, 900);
  };

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step >= s ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>{s}</div>
            {s < 2 && <div className={`flex-1 h-0.5 w-12 transition-colors ${step > s ? "bg-orange-500" : "bg-gray-200"}`} />}
          </div>
        ))}
        <span className="text-xs text-gray-400 ml-2">{step === 1 ? "Choose your role" : "Account details"}</span>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">How will you use StarBookPrime?</p>
          {roles.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${role === r.value ? r.activeClass : "border-gray-200 hover:border-gray-300 bg-white"}`}
            >
              <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${role === r.value ? "bg-white/70" : "bg-gray-100"}`}>
                <i className={`${r.icon} text-lg text-gray-700`} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm text-gray-900">{r.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{r.desc}</div>
              </div>
              {role === r.value && <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0" />}
            </button>
          ))}
          <button
            type="button"
            onClick={handleNext}
            className="w-full py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap mt-2 text-sm"
          >
            Continue as {roles.find((r2) => r2.value === role)?.label} <i className="ri-arrow-right-line ml-1" />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {/* Sign up method toggle */}
          <div className="flex p-1 bg-gray-100 rounded-xl gap-1">
            <button
              type="button"
              onClick={() => { setMethod("email"); setError(""); setOtpSent(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${method === "email" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className="ri-mail-line" /> Email
            </button>
            <button
              type="button"
              onClick={() => { setMethod("phone"); setError(""); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${method === "phone" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className="ri-smartphone-line" /> Phone
            </button>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
            <div className="relative">
              <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
          </div>

          {/* Factory name */}
          {role === "seller" && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Factory / Store Name *</label>
              <div className="relative">
                <i className="ri-store-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="e.g. TechVision Factory" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
            </div>
          )}

          {/* Influencer fields */}
          {role === "influencer" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Platform</label>
                <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 bg-white cursor-pointer">
                  {platforms.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Followers</label>
                <input type="text" value={followers} onChange={(e) => setFollowers(e.target.value)} placeholder="e.g. 250K" className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Social Handle</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">@</span>
                  <input type="text" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="yourhandle" className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* Email method */}
          {method === "email" && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password *</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters" className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Phone method */}
          {method === "phone" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number *</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1.5 px-3 py-3 border border-gray-200 rounded-xl bg-gray-50 text-sm font-medium text-gray-700 flex-shrink-0">
                    <span>🇺🇸</span> +1
                  </div>
                  <div className="relative flex-1">
                    <i className="ri-smartphone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 000-0000" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors" />
                  </div>
                </div>
              </div>
              {!otpSent ? (
                <button type="button" onClick={sendOtp} disabled={otpLoading} className="w-full py-3 border-2 border-orange-500 text-orange-500 font-bold rounded-xl hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60">
                  {otpLoading ? <><i className="ri-loader-4-line animate-spin mr-2" />Sending...</> : <><i className="ri-message-2-line mr-2" />Send Verification Code</>}
                </button>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Verification Code
                    <span className="text-xs text-green-600 font-normal ml-2"><i className="ri-checkbox-circle-fill" /> Sent!</span>
                  </label>
                  <div className="flex gap-2">
                    <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" maxLength={6} className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 text-center tracking-widest font-bold text-lg" />
                    <button type="button" onClick={sendOtp} className="px-4 py-3 text-xs text-orange-500 font-semibold border border-orange-200 rounded-xl hover:bg-orange-50 cursor-pointer whitespace-nowrap">Resend</button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Demo: enter any 4+ digit code</p>
                </div>
              )}
            </div>
          )}

          {/* Terms */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-orange-500 w-4 h-4 flex-shrink-0" />
            <span className="text-xs text-gray-500 leading-relaxed">I agree to the <span className="text-orange-500 font-semibold">Terms of Service</span> and <span className="text-orange-500 font-semibold">Privacy Policy</span></span>
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-600 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />{error}
            </div>
          )}

          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(1)} className="px-4 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line" />
            </button>
            <button type="button" onClick={handleNext} disabled={loading} className="flex-1 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm">
              {loading ? <><i className="ri-loader-4-line animate-spin mr-2" />Creating...</> : "Create Account"}
            </button>
          </div>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{" "}
            <button type="button" onClick={onSwitch} className="text-orange-500 font-bold hover:underline cursor-pointer">Sign In</button>
          </p>
        </div>
      )}
    </div>
  );
}
